import { useState, useEffect, useMemo } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "@mui/material/utils";
import Text from "@commons/Typography/Text";
import { TEXT_SIZES } from "@typings/components/text";
import type { ComboBoxOption } from "@typings/components/comboBox";

type ComboBoxProps<T = string> = {
  value: ComboBoxOption<T> | null;
  onChange: (value: ComboBoxOption<T> | null) => void;
  searchService: (query: string) => Promise<ComboBoxOption<T>[]>;
  label?: string;
  debounceTime?: number;
  minSearchLength?: number;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

export default function ComboBox<T = string>({
  value,
  onChange,
  searchService,
  label = "Pesquisar",
  debounceTime = 400,
  minSearchLength = 1,
  error,
  helperText,
  disabled,
}: ComboBoxProps<T>) {
  const [options, setOptions] = useState<ComboBoxOption<T>[]>([]);
  const [inputValue, setInputValue] = useState(value?.label ?? "");
  const [loading, setLoading] = useState(false);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (query: string) => {
        setLoading(true);
        try {
          const results =
            query.length === 0 || query.length >= minSearchLength
              ? await searchService(query)
              : [];
          setOptions(results);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceTime),
    [debounceTime, minSearchLength, searchService],
  );

  useEffect(() => {
    debouncedFetch(inputValue);
  }, [inputValue, debouncedFetch]);

  useEffect(() => {
    if (value?.label && inputValue !== value.label) {
      setInputValue(value.label);
    }
  }, [value, inputValue]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label ?? ""}
      getOptionKey={(option) => String(option.value)}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      onChange={(_, newValue: ComboBoxOption<T> | null) => onChange(newValue)}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(
        option: ComboBoxOption<T>,
        val: ComboBoxOption<T>,
      ) => option.value === val.value}
      renderInput={(params) => (
        <div className="flex flex-col">
          {label && <Text className="mb-2">{label}</Text>}
          <TextField
            {...params}
            error={error}
            helperText={helperText ?? (error ? "Campo obrigatório" : "")}
            size="small"
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                className: `${params.inputProps.className ?? ""} ${TEXT_SIZES["sm"]} p-1`,
              },
            }}
          />
        </div>
      )}
    />
  );
}
